import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Stack,
  alpha,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  CreditCard as CreditCardIcon,
  EmailOutlined,
  LockOutlined,
  LoginOutlined,
  Nfc as NfcIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { BRAND } from "../../constants/brand";
import PoweredByBrand from "../../components/brand/PoweredByBrand";
import { useAuth } from "../../contexts/AuthContext";

const schema = yup.object({
  email: yup.string().email("Invalid email format").required("Email is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

type FormData = yup.InferType<typeof schema>;

const FEATURES = [
  { icon: <NfcIcon fontSize="small" />, text: "NFC cards & quick recharge" },
  { icon: <CreditCardIcon fontSize="small" />, text: "Sales, inventory & reports" },
  { icon: <SecurityIcon fontSize="small" />, text: "Secure role-based access" },
];

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [requires2fa, setRequires2fa] = React.useState(false);
  const [totpCode, setTotpCode] = React.useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (data: FormData) => {
    setError("");
    setIsLoading(true);
    try {
      const result = await login(
        data.email,
        data.password,
        requires2fa ? totpCode : undefined
      );
      if (result.requires2fa) {
        setRequires2fa(true);
        setError("");
        return;
      }
      navigate("/dashboard");
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Login failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const brandPanelOuterSx = {
    bgcolor: "#FFFFFF",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    px: { xs: 3, md: 5, lg: 6 },
    py: { xs: 4, md: 6 },
    minHeight: { xs: "auto", md: "100vh" },
    borderRight: { md: "1px solid" },
    borderColor: { md: "divider" },
  };

  const brandPanelContent = (
    <Box
      sx={{
        width: "100%",
        maxWidth: 400,
        display: "flex",
        flexDirection: "column",
        alignItems: { xs: "center", md: "flex-start" },
        textAlign: { xs: "center", md: "left" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 320,
          height: { xs: 200, md: 240 },
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "center", md: "flex-start" },
          mb: { xs: 2.5, md: 3 },
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={BRAND.logo.large}
          alt={BRAND.name}
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
            display: "block",
            objectFit: "contain",
            objectPosition: { xs: "center", md: "left center" },
          }}
        />
      </Box>

      <Typography
        variant="h5"
        fontWeight={800}
        color="text.secondary"
        sx={{
          width: "100%",
          lineHeight: 1.5,
          display: { xs: "none", md: "block" },
        }}
      >
        {BRAND.productNameAdmin}
      </Typography>
      <Typography
        variant="subtitle1"
        color="primary.main"
        fontWeight={700}
        sx={{
          width: "100%",
          display: { xs: "none", md: "block" },
          mt: 0.5,
          mb: 0.5,
        }}
      >
        {BRAND.tagline}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          width: "100%",
          lineHeight: 1.5,
          display: { xs: "none", md: "block" },
        }}
      >
        Manage cards, recharges, and branch operations from one admin portal.
      </Typography>

      <Stack
        spacing={1.5}
        sx={{
          mt: 3,
          width: "100%",
          display: { xs: "none", md: "flex" },
          alignItems: "flex-start",
        }}
      >
        {FEATURES.map((f) => (
          <Stack
            key={f.text}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ width: "100%" }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: 1.5,
                bgcolor: alpha(BRAND.colors.orange500, 0.12),
                color: BRAND.colors.orange600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {f.icon}
            </Box>
            <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500 }}>
              {f.text}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#FAFBFC",
      transition: "box-shadow 0.2s, border-color 0.2s",
      "&:hover": { bgcolor: "#FFFFFF" },
      "&.Mui-focused": {
        bgcolor: "#FFFFFF",
        boxShadow: `0 0 0 3px ${alpha(BRAND.colors.orange500, 0.15)}`,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: BRAND.colors.orange600,
    },
  };

  const loginForm = (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3.5, sm: 4.5 },
        width: "100%",
        maxWidth: 440,
        borderRadius: 4,
        border: "1px solid",
        borderColor: alpha(BRAND.colors.orange500, 0.12),
        boxShadow: `0 8px 40px ${alpha(BRAND.colors.navy, 0.08)}`,
        bgcolor: "#FFFFFF",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${BRAND.colors.orange500}, ${BRAND.colors.orange400})`,
        },
      }}
    >
      <Stack spacing={3} sx={{ pt: 1 }}>
        <Box>
          <Box
            component="img"
            src={BRAND.logo.md}
            alt={BRAND.name}
            sx={{
              width: 48,
              height: 48,
              objectFit: "contain",
              mb: 2,
              display: { md: "none" },
            }}
          />
          <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ letterSpacing: "-0.02em" }}>
            Welcome back
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>
            {BRAND.productNameAdmin} · {BRAND.tagline}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Sign in to your admin account
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2.5}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Email address"
                  autoComplete="email"
                  autoFocus
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              )}
            />
          </Stack>

          {requires2fa && (
            <TextField
              fullWidth
              label="Authenticator code"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              helperText="Enter the 6-digit code from your authenticator app"
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }}
              sx={{ ...inputSx, mt: 2.5 }}
            />
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <LoginOutlined />
              )
            }
            sx={{
              mt: 3.5,
              py: 1.6,
              borderRadius: 2,
              fontWeight: 700,
              fontSize: "1rem",
              textTransform: "none",
              bgcolor: BRAND.colors.orange500,
              boxShadow: `0 4px 16px ${alpha(BRAND.colors.orange600, 0.4)}`,
              "&:hover": {
                bgcolor: BRAND.colors.orange600,
                boxShadow: `0 8px 24px ${alpha(BRAND.colors.orange700, 0.35)}`,
              },
              "&.Mui-disabled": {
                bgcolor: alpha(BRAND.colors.orange500, 0.6),
                color: "#FFFFFF",
              },
            }}
          >
            {isLoading ? "Signing in…" : "Sign in"}
          </Button>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", pt: 0.5 }}
        >
          Secure access for authorized staff only ·{" "}
          <Box
            component="a"
            href="/"
            sx={{ color: BRAND.colors.orange600, fontWeight: 600, textDecoration: "none" }}
          >
            Back to home
          </Box>
        </Typography>
        <PoweredByBrand sx={{ pt: 1.5 }} />
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: { xs: "column", md: "row" }, bgcolor: "#FFFFFF" }}>
      <Box sx={{ ...brandPanelOuterSx, display: { xs: "none", md: "flex" }, flex: "1 1 50%" }}>
        {brandPanelContent}
      </Box>

      <Box
        sx={{
          ...brandPanelOuterSx,
          display: { xs: "flex", md: "none" },
          flex: "none",
          borderRight: 0,
        }}
      >
        {brandPanelContent}
      </Box>

      <Box
        sx={{
          flex: { xs: "1 1 auto", md: "1 1 50%" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: BRAND.colors.orange50,
          backgroundImage: `radial-gradient(ellipse 80% 60% at 50% 0%, ${alpha(BRAND.colors.orange100, 0.9)} 0%, transparent 70%)`,
          p: { xs: 3, sm: 5, md: 6 },
          minWidth: 0,
          minHeight: { md: "100vh" },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 440 }}>{loginForm}</Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
